import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { fetchEventById, searchEvents, fetchComments, createComment } from '@/apiEndpoints';
import { Calendar } from '@heroui/calendar';
import { CalendarDate, isSameDay } from '@internationalized/date';
import { useAuth } from '@/contexts/auth-context';
import Share from '@/icons/Share';
import Report from '@/icons/Report';
import LikeButton from '@/components/LikeButton';
import ShareModal from '@/components/ShareModal';
import moment from 'moment';
import Minus from '@/icons/Minus';
import Plus from '@/icons/Plus';
import Location from '@/icons/Location';
import Send from '@/icons/Send';
import Loading from '@/icons/Loading';
import { Input } from '@heroui/input';
import { Button } from '@heroui/react';
import Avatar from '@/components/Avatar';
import EventAuthorizationGuard from '@/components/EventAuthorizationGuard';
import EventMap from '@/components/EventMap';
import { currencySymbol } from '@/utils/currency';
import { btnGhost, card, darkPanel, eyebrow, glow, pageBg, sectionTitle, surface, wrap } from '@/components/ui/design';

const blockTitle = 'mb-3 text-2xl font-bold tracking-[-0.03em] text-grey10 dark:text-white';
const sendBtn = 'flex h-9 w-9 items-center justify-center rounded-full transition-colors hover:bg-grey95 disabled:cursor-not-allowed disabled:opacity-60 dark:hover:bg-white/10';
const commentInputClassNames = {
    inputWrapper: 'h-12 rounded-2xl bg-grey99 pr-1 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60',
    input: 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted',
};

const TABS = ['Date & Time', 'Location', 'About'];

export default function PromotedEventDetails() {
    const { id } = useParams();
    const [ticketCounts, setTicketCounts] = useState({});
    const [tab, setTab] = useState(TABS[0]);
    const [showAllTimes, setShowAllTimes] = useState(false);
    const [selectedTime, setSelectedTime] = useState(null);
    const [showAllAbout, setShowAllAbout] = useState(false);
    const [commentInput, setCommentInput] = useState('');
    const [replyInputs, setReplyInputs] = useState<{ [key: string]: string }>({});
    const [showShareModal, setShowShareModal] = useState(false);

    const { isAuthenticated } = useAuth();

    // Fetch event details
    const { data: event, isLoading, isError } = useQuery({
        queryKey: ['event', id],
        queryFn: () => fetchEventById(id as string),
        enabled: !!id,
    });

    // Tickets are now included in event data
    const tickets = event?.data?.tickets || [];
    const isTicketsLoading = isLoading; // Use event loading state

    // Fetch related events (by category or organizer)
    const { data: relatedEventsData = { events: [] }, isLoading: isRelatedLoading } = useQuery({
        queryKey: ['relatedEvents', event?.data?.category, event?.data?.organizer?._id],
        queryFn: () => {
            if (event?.data?.category) {
                return searchEvents({ category: event?.data?.category, limit: 4 });
            } else if (event?.data?.userId) {
                return searchEvents({ organizerId: event?.data?.userId, limit: 4 });
            }
            return { events: [] };
        },
        enabled: !!event,
    });
    const relatedEvents = (relatedEventsData?.events || []).filter((e: any) => e._id !== event?.data?._id).slice(0, 4);

    // Fetch comments/messages for this event
    const { data: commentsData = { data: [] }, refetch: refetchComments } = useQuery({
        queryKey: ['comments', event?.data?._id],
        queryFn: () => fetchComments(event?.data?._id, 1, 50), // Fetch first 50 comments
        enabled: !!event?.data?._id,
    });
    const comments = commentsData?.data || [];

    // Post comment mutation
    const postComment = useMutation({
        mutationFn: async (text: string) => {
            return createComment(event?.data?._id, text);
        },
        onSuccess: (newComment) => {
            // Refetch comments to get the updated list
            refetchComments();
            setCommentInput('');
        },
        onError: (error) => {
            console.error('Failed to post comment:', error);
            // You could add a toast notification here
        },
    });

    // Post reply mutation
    const postReply = useMutation({
        mutationFn: async ({ parentCommentId, text }: { parentCommentId: string; text: string }) => {
            return createComment(event?.data?._id, text, parentCommentId);
        },
        onSuccess: (reply, { parentCommentId }) => {
            refetchComments();
            setReplyInputs((r) => ({ ...r, [parentCommentId]: '' }));
        },
        onError: (error) => {
            console.error('Failed to post reply:', error);
            // You could add a toast notification here
        },
    });

    // Handle like changes
    const handleLikeChange = (likes: number, isLiked: boolean) => {
        console.log(`Event like status changed: ${likes} likes, isLiked: ${isLiked}`);
        // You can add additional logic here like updating local state, showing notifications, etc.
    };

    if (isLoading) {
        return (
            <div className={`min-h-screen flex flex-col ${pageBg}`}>
                <Header />
                <div className="flex-1 flex items-center justify-center text-lg font-semibold text-grey40 dark:text-nightMuted">Loading event...</div>
                <Footer />
            </div>
        );
    }
    if (isError || !event) {
        return (
            <div className={`min-h-screen flex flex-col ${pageBg}`}>
                <Header />
                <div className="flex-1 flex items-center justify-center text-lg font-semibold text-red-500 dark:text-red-400">Event not found.</div>
                <Footer />
            </div>
        );
    }

    return (
        <div className={`min-h-screen flex flex-col overflow-x-clip ${pageBg}`}>
            <Header />
            <div className={`${wrap} relative isolate flex flex-col gap-8 pb-16 pt-6 sm:gap-10 sm:pb-24 sm:pt-10`}>
                <div className={glow} />
                {/* Banner */}
                <div className="relative h-[220px] w-full overflow-hidden rounded-[28px] bg-grey10 sm:h-[464px] sm:rounded-[36px]">
                    {/* Blurred background image */}
                    <img
                        src={event?.data?.attachments?.banner?.link || '/banner.png'}
                        alt="Event Banner Background"
                        className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl"
                    />
                    {/* Main sharp image */}
                    <img
                        src={event?.data?.attachments?.banner?.link || '/banner.png'}
                        alt="Event Banner"
                        className="relative z-10 w-full h-full object-contain bg-transparent object-center"
                    />
                </div>
                {/* Main Content */}
                <div className="flex flex-col gap-8 lg:flex-row lg:gap-12">
                    {/* Left: Main Info */}
                    <div className="flex-1 min-w-0">
                        {/* Tabs */}
                        <div className="mb-6 hidden flex-wrap items-center gap-2 sm:flex">
                            {TABS.map(t => (
                                <a
                                    key={t}
                                    className="inline-flex h-10 items-center rounded-full bg-grey99 px-4 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10"
                                >{t}</a>
                            ))}
                            <div className="flex-1" />
                            <button
                                onClick={() => setShowShareModal(true)}
                                className={`${btnGhost} ml-auto h-10 px-4`}
                            >
                                Share <Share />
                            </button>
                        </div>
                        {/* Show all sections at the same time if event is loaded */}
                        {(!event || !event.data) ? null : (
                            <>
                                {/* Title, Badges, Like */}
                                <div className="mb-4 flex flex-wrap items-center gap-2">
                                    <span className="rounded-full bg-[#CB0000] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-white">Promoted</span>
                                    {event?.data?.isNew && <span className="rounded-full bg-[#CB0000] px-3 py-1.5 text-xs font-bold uppercase tracking-[0.08em] text-white">New</span>}
                                    <LikeButton
                                        eventId={event?.data?._id || event?.data?.id || ''}
                                        initialLikes={event?.data?.likes || 0}
                                        likeStats={event?.data?.likeStats}
                                        className="h-8 rounded-full bg-grey99 px-3 text-xs font-semibold text-primary dark:bg-white/[0.06] dark:text-nightAccent"
                                        refetchQueries={['event', 'relatedEvents']}
                                        onLikeChange={handleLikeChange}
                                    />
                                </div>
                                <h1 className={`${sectionTitle} mb-8 break-words`}>{event?.data?.title}</h1>
                                {/* Date & Time Section */}
                                <div className={`${surface} mb-8 p-5 sm:p-6`}>
                                    <div className={eyebrow}>Date & Time</div>
                                    <div className="mt-2 text-lg font-semibold tracking-[-0.01em] text-grey10 dark:text-white">{moment(event?.data?.times?.[0]?.startDate).format('dddd, MMM D, YYYY h:mm A')} to {moment(event?.data?.times?.[0]?.endDate).format('h:mm A')}</div>
                                </div>
                                {/* Location Section */}
                                <div className="mb-10">
                                    <div className={blockTitle}>Location</div>
                                    <div className="mb-4 flex items-start gap-2 text-base font-semibold text-grey20 dark:text-nightTextSecondary">
                                        <Location />
                                        {event?.data?.showOnlyCity === 1
                                            ? event?.data?.address?.city || event?.data?.address?.fullAddress
                                            : event?.data?.address?.fullAddress
                                        }
                                    </div>
                                    {event?.data?.lat && event?.data?.lng ? (
                                        <EventMap
                                            lat={event.data.lat}
                                            lng={event.data.lng}
                                            title={event.data.title}
                                            address={event.data.address}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                                {/* About Section */}
                                <div className="mb-10">
                                    <div className={blockTitle}>About</div>
                                    <div className="whitespace-pre-line text-base leading-relaxed text-grey40 dark:text-nightMuted">{event?.data?.description}</div>
                                </div>
                                {/* Comments Section */}
                                {event?.data?.commentsDisabled !== 1 && (
                                    <div className="mb-6">
                                        <div className={blockTitle}>
                                            Comments {comments.length > 0 && `(${comments.length})`}
                                        </div>

                                        {/* Comment input - only for logged in users */}
                                        {isAuthenticated ? (
                                            <div className="mb-4">
                                                <Input
                                                    classNames={commentInputClassNames}
                                                    placeholder="Add a comment..."
                                                    value={commentInput}
                                                    onChange={e => setCommentInput(e.target.value)}
                                                    disabled={postComment.isPending}
                                                    endContent={
                                                        <button
                                                            className={sendBtn}
                                                            onClick={() => commentInput && postComment.mutate(commentInput)}
                                                            disabled={!commentInput || postComment.isPending}
                                                        >
                                                            {postComment.isPending ? (
                                                                <Loading
                                                                    width={20}
                                                                    height={20}
                                                                    color="#0EA5E9"
                                                                />
                                                            ) : (
                                                                <Send
                                                                    width={20}
                                                                    height={20}
                                                                    color={commentInput && !postComment.isPending ? "#0EA5E9" : "#9CA3AF"}
                                                                />
                                                            )}
                                                        </button>
                                                    }
                                                />
                                            </div>
                                        ) : (
                                            <div className={`${surface} mb-4 p-6 text-center`}>
                                                <p className="mb-3 text-sm text-grey40 dark:text-nightMuted">Please log in to post a comment</p>
                                                <Link to="/login" className="inline-flex h-10 items-center justify-center rounded-full bg-grey10 px-5 text-sm font-semibold text-white transition-colors hover:bg-black dark:bg-white dark:text-nightBg dark:hover:bg-grey90">
                                                    Login to comment
                                                </Link>
                                            </div>
                                        )}

                                        <div className="space-y-4">
                                            {comments.length === 0 ? (
                                                <div className={`${surface} py-10 text-center text-grey40 dark:text-nightMuted`}>
                                                    <p className="text-sm">No comments yet. Be the first to comment!</p>
                                                </div>
                                            ) : (
                                                comments.map((c: any, i: number) => (
                                                    <div key={c.id || c._id || i} className={`${card} p-5`}>
                                                        <div className="flex items-start gap-3 mb-1">
                                                            <Avatar
                                                                src={c.userInfo?.profileImage}
                                                                name={c.userInfo?.firstName}
                                                                seed={c.userInfo?.userId || c.userInfo?._id || c.userInfo?.id || c.userInfo?.email}
                                                                size={40}
                                                                className="ring-1 ring-black/[0.06] dark:ring-white/10"
                                                            />

                                                            <div className="flex flex-col">
                                                                <div className="text-[15px] font-bold text-grey10 dark:text-white">{c.userInfo?.firstName || 'Anonymous'}</div>
                                                                <div className="text-xs font-semibold text-grey50 dark:text-nightMuted">{moment(c.createdAt).fromNow()}</div>
                                                            </div>
                                                        </div>
                                                        <div className="mb-2 mt-2 text-[15px] leading-relaxed text-grey20 dark:text-nightTextSecondary">{c.body}</div>
                                                        {isAuthenticated ? (
                                                            <button
                                                                className="mb-2 rounded-full bg-grey99 px-3 py-1 text-sm font-semibold text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightTextSecondary dark:hover:bg-white/10"
                                                                onClick={() => setReplyInputs((r) => ({ ...r, [c.id || c._id]: r[c.id || c._id] ? '' : 'show' }))}
                                                            >Reply</button>
                                                        ) : (
                                                            <Link to="/login" className="mb-2 text-xs font-semibold text-primary hover:underline dark:text-nightAccent">
                                                                Login to reply
                                                            </Link>
                                                        )}
                                                        {/* Reply input */}
                                                        {replyInputs[c.id || c._id] && (
                                                            <div className="mb-4 ml-4 sm:ml-8">
                                                                <Input
                                                                    classNames={commentInputClassNames}
                                                                    placeholder="Write a reply..."
                                                                    value={replyInputs[c.id || c._id] === 'show' ? '' : (replyInputs[c.id || c._id] || '')}
                                                                    onChange={e => setReplyInputs((r) => ({ ...r, [c.id || c._id]: e.target.value }))}
                                                                    disabled={postReply.isPending}
                                                                    endContent={
                                                                        <button
                                                                            className={sendBtn}
                                                                            onClick={() => {
                                                                                const replyText = replyInputs[c.id || c._id];
                                                                                if (replyText && replyText !== 'show') {
                                                                                    postReply.mutate({ parentCommentId: c.id || c._id, text: replyText });
                                                                                }
                                                                            }}
                                                                            disabled={!replyInputs[c.id || c._id] || replyInputs[c.id || c._id] === 'show' || postReply.isPending}
                                                                        >
                                                                            {postReply.isPending ? (
                                                                                <Loading
                                                                                    width={20}
                                                                                    height={20}
                                                                                    color="#0EA5E9"
                                                                                />
                                                                            ) : (
                                                                                <Send
                                                                                    width={20}
                                                                                    height={20}
                                                                                    color={replyInputs[c.id || c._id] && replyInputs[c.id || c._id] !== 'show' && !postReply.isPending ? "#0EA5E9" : "#9CA3AF"}
                                                                                />
                                                                            )}
                                                                        </button>
                                                                    }
                                                                />
                                                            </div>
                                                        )}
                                                        {/* Replies */}
                                                        {c.replies?.length > 0 && (
                                                            <div className="ml-4 mt-3 space-y-2 sm:ml-8">
                                                                {c.replies.map((r: any, j: number) => (
                                                                    <div key={r.id || r._id || j} className="rounded-2xl bg-grey99 p-4 dark:bg-white/[0.04]">
                                                                        <div className="flex items-start gap-2 mb-1">
                                                                            <Avatar
                                                                                src={r.userInfo?.profileImage}
                                                                                name={r.userInfo?.firstName}
                                                                                seed={r.userInfo?.userId || r.userInfo?._id || r.userInfo?.id || r.userInfo?.email}
                                                                                size={40}
                                                                                className="ring-1 ring-black/[0.06] dark:ring-white/10"
                                                                            />
                                                                            <div className="flex flex-col">
                                                                                <div className="text-[15px] font-bold text-grey10 dark:text-white">{r.userInfo?.firstName || 'Anonymous'}</div>
                                                                                <div className="text-xs font-semibold text-grey50 dark:text-nightMuted">{moment(r.createdAt).fromNow()}</div>
                                                                            </div>
                                                                        </div>
                                                                        <div className="mb-1 mt-2 text-[15px] leading-relaxed text-grey20 dark:text-nightTextSecondary">{r.body}</div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {/* Right: Sidebar */}
                    <div className="flex w-full flex-col gap-6 lg:sticky lg:top-24 lg:w-[360px] lg:self-start">
                        <div className={`${darkPanel} flex flex-col gap-4 p-6 sm:p-8`}>
                            <div className="pointer-events-none absolute -right-16 -top-24 -z-10 h-56 w-56 rounded-full bg-nightAccent/25 blur-[80px] max-md:hidden" />
                            <div className="flex flex-col gap-2">
                                <span className="text-[40px] font-bold leading-none tracking-[-0.04em] text-white">{currencySymbol(event?.data?.currency)}{event?.data?.price || '52'}</span>
                                {event?.data?.promotedCouponCode && <div className="text-sm text-white/60">Coupon Code: <span className="rounded-full bg-white/10 px-2.5 py-0.5 font-bold text-white">{event?.data?.promotedCouponCode}</span></div>}
                                <div className="text-sm text-white/60">Sales From: <span className="font-bold text-white">{
                                    event?.data?.promotedSellStart && event?.data?.promotedSellEnd
                                        ? `${moment(event?.data?.promotedSellStart).format('MMM D, YYYY')} to ${moment(event?.data?.promotedSellEnd).format('MMM D, YYYY')}`
                                        : ''
                                }</span></div>
                                <Button onPress={() => { if (event?.data?.promotedEventLink) window.location.href = event?.data?.promotedEventLink; }} className="mt-4 h-12 w-full rounded-full bg-nightAccent px-6 text-sm font-semibold text-nightBg hover:bg-primary70">Open Event Link</Button>
                            </div>
                        </div>
                        <div className={`${card} flex flex-col p-6`}>
                            <div className={`${eyebrow} mb-3`}>Promoted by</div>
                            <div className="flex items-center gap-3 rounded-2xl bg-grey99 p-4 dark:bg-white/[0.04]">
                                {!event?.data?.info?.orgName ? (
                                    <Link
                                        to={`/user/${event?.data?.info.id}`}
                                        className="flex items-center gap-3 hover:underline"
                                    >
                                        <img
                                            src={event?.data?.info.profileImage || '/host-avatar.png'}
                                            alt={event?.data?.info.firstName}
                                            className="h-12 w-12 rounded-full object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
                                        />
                                        <span className="text-sm font-semibold text-grey10 dark:text-white">{event?.data?.info.firstName}</span>
                                    </Link>
                                ) : null}
                                {event?.data?.info?.orgName ? (
                                    <Link
                                        to={`/organization/${event?.data?.info._id}`}
                                        className="flex items-center gap-3 hover:underline"
                                    >
                                        <img
                                            src={event?.data?.info?.profileImage || event?.data?.info?.attachments?.avatar?.link || '/host-avatar.png'}
                                            alt={event?.data?.info?.orgName}
                                            className="h-12 w-12 rounded-full object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
                                        />
                                        <span className="text-sm font-semibold text-grey10 dark:text-white">{event?.data?.info?.orgName}</span>
                                    </Link>
                                ) : null}
                            </div>
                            {isAuthenticated && (
                                <button className="mt-4 flex items-center gap-2 text-left text-xs font-medium text-grey50 underline dark:text-nightMuted">
                                    <Report />
                                    Report This Event
                                </button>
                            )}
                        </div>
                    </div>
                </div>
                {/* More Events Like This */}
                {relatedEvents.length > 0 && (
                    <div className="mt-8 sm:mt-12">
                        <div className={`${sectionTitle} mb-8`}>More events Like this</div>
                        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                            {relatedEvents.map((rel: any, i: number) => {
                                const slug = rel.title.replace(/\W+/g, '-').toLowerCase();
                                return (
                                    <Link key={i} to={`/event/${slug}`} className="block group">
                                        <div className={`${card} relative flex h-full flex-col overflow-hidden transition-transform duration-300 group-hover:-translate-y-1`}>
                                            <div className="relative">
                                                <img src={rel.image} alt={rel.title} className="h-44 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
                                                {rel.isNew && (
                                                    <span className="absolute right-3 top-3 rounded-full bg-white/90 px-3 py-1 text-xs font-bold uppercase tracking-[0.08em] text-[#0a0a0a]">New</span>
                                                )}
                                                <LikeButton
                                                    eventId={rel._id || rel.id || ''}
                                                    initialLikes={rel.likes || 0}
                                                    likeStats={rel.likeStats}
                                                    className="absolute bottom-3 right-3 rounded-full bg-white/90 px-3 py-1 text-xs text-grey10 dark:bg-black/60 dark:text-white"
                                                    refetchQueries={['event', 'relatedEvents']}
                                                    onLikeChange={(likes, isLiked) => {
                                                        console.log(`Related event ${rel._id || rel.id} like status changed: ${likes} likes, isLiked: ${isLiked}`);
                                                    }}
                                                />
                                            </div>
                                            <div className="flex flex-1 flex-col gap-1.5 px-5 pb-5 pt-4">
                                                <div className="mb-1 line-clamp-2 text-lg font-bold leading-tight tracking-[-0.02em] text-grey10 dark:text-white">{rel.title}</div>
                                                <div className="text-sm text-grey40 dark:text-nightMuted">{rel.date}</div>
                                                <div className="text-sm text-grey40 dark:text-nightMuted">{rel.location}</div>
                                                <div className="flex items-center justify-between mt-auto pt-2">
                                                    <span className="text-base font-bold text-primary dark:text-nightAccent">{rel.price}</span>
                                                    <div className="flex gap-1">
                                                        <button
                                                            className="flex h-9 w-9 items-center justify-center rounded-full bg-grey99 text-grey20 transition-colors hover:bg-grey95 dark:bg-white/[0.06] dark:text-nightMuted dark:hover:bg-white/10"
                                                            tabIndex={-1}
                                                            onClick={e => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                const shareUrl = rel.handle ? `${window.location.origin}/e/${rel.handle}` : `${window.location.origin}/event/${rel._id || rel.id}`;
                                                                navigator.clipboard.writeText(shareUrl);
                                                            }}
                                                            title="Share event"
                                                        >
                                                            <Share width={14} height={14} />
                                                        </button>
                                                        <button className="flex h-9 w-9 items-center justify-center rounded-full bg-grey10 text-lg font-bold text-white dark:bg-white dark:text-nightBg" tabIndex={-1} onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.preventDefault()}>+</button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>

            {/* Share Modal */}
            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                eventTitle={event?.data?.title || 'Event'}
                eventUrl={typeof window !== 'undefined' ? window.location.href : ''}
                eventImage={event?.data?.attachments?.banner?.link}
                eventDescription={event?.data?.description}
            />

            <Footer />
        </div>
    );
} 